import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'

/** Resumen de una ejecución de recálculo (coincide con ResumenRecalculo del backend). */
export interface ResumenRecalculo {
  ejecucionId: string
  modo: 'simulacion' | 'real'
  totalClientes: number
  totalCambios: number
  pctAfectado: number
  detenida: boolean
  motivoParada: string | null
  excluidos: number
}

/** Fila del histórico de ejecuciones. */
export interface EjecucionRecalculo {
  id: string
  modo: 'simulacion' | 'real'
  totalClientes: number
  totalCambios: number
  pctAfectado: number
  detenida: boolean
  motivoParada: string | null
  iniciadaEn: string
  terminadaEn: string | null
}

/** Un cambio concreto de S propuesto por el recálculo. */
export interface CambioRecalculo {
  id: string
  contactId: string
  campo: string
  valorAnterior: string | null
  valorNuevo: string | null
  motivo: string
}

/** Histórico de ejecuciones del recálculo (auditoría, A.2.1 · §9). */
export function useEjecucionesRecalculo() {
  const api = useApi()
  return useQuery<EjecucionRecalculo[], ErrorApi>({
    queryKey: ['etiquetas', 'ejecuciones'],
    queryFn: ({ signal }) =>
      api.pedir<EjecucionRecalculo[]>('/api/etiquetas/ejecuciones', { senal: signal }),
  })
}

/** Cambios de una ejecución concreta, bajo demanda. */
export function useCambiosEjecucion(ejecucionId: string | null) {
  const api = useApi()
  return useQuery<CambioRecalculo[], ErrorApi>({
    queryKey: ['etiquetas', 'cambios', ejecucionId],
    queryFn: ({ signal }) =>
      api.pedir<CambioRecalculo[]>(`/api/etiquetas/ejecuciones/${ejecucionId}/cambios`, {
        senal: signal,
      }),
    enabled: ejecucionId !== null,
  })
}

/**
 * Lanza un recálculo en modo SIMULACIÓN: calcula las S nuevas y las registra en
 * el log/auditoría, pero NO toca Holded. Es la única forma de recálculo manual
 * desde el panel (la escritura real se habilita por configuración tras 2 ciclos).
 */
export function useRecalcularSimulacion() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<ResumenRecalculo, ErrorApi, void>({
    mutationFn: () =>
      api.pedir<ResumenRecalculo>('/api/etiquetas/recalcular/simulacion', { metodo: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['etiquetas', 'ejecuciones'] }),
  })
}
